// The 422 body's error array is OrbitalX.Dto.Common.ValidationErrorDto. `FieldName` is nullable,
// not merely optional: the API's worker serializer sets no DefaultIgnoreCondition, so an error with
// no associated field arrives as an explicit `"FieldName": null` rather than with the property
// omitted (ORB-585). The generated declaration says exactly that, so this is no longer restated.
export type { ValidationErrorDto as ValidationError } from '@/types/api-generated'
import type { ValidationErrorDto as ValidationError } from '@/types/api-generated'

interface ApiErrorResponse {
  // A 422 carries the validation errors as a real array. Older API builds double-encoded them as a
  // JSON string, so both are accepted — the two repos deploy independently and neither ordering
  // should break error toasts (ORB-390).
  error?: string | ValidationError[]
  stackTrace?: string
}

/**
 * One predicate for both wire shapes.
 *
 * Requires Title and Message to be non-empty strings rather than merely present: an entry carrying
 * `Title: ""` would otherwise pass and render as a blank toast or a blank popover row, which reads as
 * a UI bug rather than as the malformed payload it is.
 *
 * FieldName must be a string when it names a field, since it is used to route the user to a tab — but
 * absent is `null` here, not `undefined`. The API writes the null explicitly (see ValidationError
 * above), and most business-rule errors have no field at all: every CSV row error, and every
 * validator calling the two-argument AddError overload. Rejecting null therefore discarded the
 * majority of real errors and the user was shown nothing, on every path that renders a 422 — the
 * global toast, audience import, sign-off, approved domains, the tracking script form (ORB-585).
 */
function isValidationErrorEntry(value: unknown): value is ValidationError {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<ValidationError>
  if (typeof candidate.Title !== 'string' || candidate.Title.trim() === '') return false
  if (typeof candidate.Message !== 'string' || candidate.Message.trim() === '') return false
  return candidate.FieldName === undefined
    || candidate.FieldName === null
    || typeof candidate.FieldName === 'string'
}

/**
 * Coerce an API error payload to text (ORB-537).
 *
 * `ApiResponse.error` is declared `string`, but the API's envelope carries business-rule failures as
 * a real JSON array — `{ error: [ {Title, Message, FieldName}, … ] }` (ORB-390) — so the raw value is
 * frequently not a string at all. `response.json()` is `any`, so nothing type-checks the difference
 * and the mismatch only shows up when a consumer calls a string method and throws.
 *
 * Re-serializes rather than flattening to a single message: the structure has to survive for
 * parseValidationErrors below, which already accepts exactly this JSON-string form. Null/undefined
 * become '' so callers can keep using a falsy check to fall back to their own default text.
 */
export function stringifyApiError(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  try {
    // JSON.stringify returns undefined for values with no JSON representation (a bare function or
    // symbol); '' routes those to the caller's fallback rather than the string "undefined".
    return JSON.stringify(value) ?? ''
  } catch {
    // Circular structure. String() is lossy, but handing back text beats throwing here — this
    // function exists precisely so a malformed payload degrades instead of crashing the caller.
    return String(value)
  }
}

export function parseValidationErrors(errorResponse: ApiErrorResponse): ValidationError[] {
  if (!errorResponse.error) return []

  // Current shape: the array arrives as JSON, no unwrapping needed.
  if (Array.isArray(errorResponse.error)) {
    return errorResponse.error.filter(isValidationErrorEntry)
  }

  try {
    // Legacy shape: the error field contains a JSON string of validation errors.
    const errors: unknown = JSON.parse(errorResponse.error)
    // Both shapes filter per entry. The array branch used to be all-or-nothing, so one malformed
    // error discarded every valid one alongside it and the user saw nothing at all.
    return Array.isArray(errors) ? errors.filter(isValidationErrorEntry) : []
  } catch {
    // If parsing fails, return the raw error as a single validation error
    return [{
      Title: 'Error',
      Message: errorResponse.error
    }]
  }
}

/**
 * Turn anything thrown by an API call into the lines fit to show a user.
 *
 * The API's envelope carries business-rule failures as an array of `{Title, Message, FieldName}`
 * (ORB-390), and `stringifyApiError` deliberately re-serializes that array so the structure survives
 * for `parseValidationErrors`. The cost is that `Error.message` frequently holds raw JSON, so a
 * `catch` that renders `err.message` directly shows the user something like
 * `[{"Title":"Auth Connection Error","Message":"Domain 'orbitalx.io' is already used…","FieldName":"EmailDomains"}]`
 * — the real message is in there, wrapped in punctuation nobody should have to read past.
 *
 * Extracts the Message fields when the payload parses as validation errors, and otherwise hands back
 * the original text unchanged as a single entry. Returns a list rather than newline-joined text on
 * purpose: joining would force every caller's container to honour `
`, and a container that does not
 * silently runs the messages together. A list makes the caller render each message as its own
 * element, so separation is structural instead of a formatting assumption made here.
 *
 * Always returns at least one entry. Use this in `catch` blocks that display an error inline. It is
 * not for field-level routing — that is `setFormValidationErrors`, which places each message on the
 * field its `FieldName` names.
 */
/**
 * The sentence inside one of the API's business envelopes (see getDisplayErrorMessages), or null when the text is not
 * one. apiClient reads it for a 412, and getDisplayErrorMessages for every envelope.
 */
export function envelopeMessage(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed.startsWith('{')) return null
  try {
    const envelope: unknown = JSON.parse(trimmed)
    if (envelope && typeof envelope === 'object' && 'message' in envelope) {
      const message = (envelope as { message: unknown }).message
      if (typeof message === 'string' && message.trim() !== '') return message
    }
  } catch {
    // Not JSON after all — a message that merely begins with a brace.
  }
  return null
}

export function getDisplayErrorMessages(error: unknown, fallback = 'Something went wrong'): string[] {
  const raw = error instanceof Error ? error.message : stringifyApiError(error)
  const trimmed = raw.trim()
  if (!trimmed) return [fallback]

  // ⚑ The other envelope the API sends. Every non-422 business branch in `ErrorHandlingMiddleware`
  // — 402 InsufficientCredits, 403 Forbidden, 409 TurnAlreadyActive, 410 ConversationExpired, 412
  // ConcurrencyConflict — serializes an OBJECT into the wrapper's `error` field, so what arrives here
  // is the string `{"error":"InsufficientCredits","message":"This costs 340 credits…",…}`. Without
  // this branch that whole string is what the user reads. The object always carries `message`, which
  // is the sentence written for them; the machine-readable siblings are for the caller, not the
  // reader.
  if (trimmed.startsWith('{')) return [envelopeMessage(trimmed) ?? raw]

  // Only a serialized array can be validation errors, so anything else is already the message and is
  // returned as-is. Two reasons to check rather than handing everything to parseValidationErrors:
  // it keeps the common case ("Failed to fetch") off a JSON.parse that throws and is caught, and it
  // stops this function depending on how parseValidationErrors chooses to treat unparseable input —
  // it currently returns that input as a single entry, but returning [] would be just as reasonable
  // and would silently change what the user sees here.
  if (!trimmed.startsWith('[')) return [raw]

  const messages = parseValidationErrors({ error: trimmed })
    .map(validationError => validationError.Message)
    .filter(message => message.trim() !== '')

  // Falls back to the raw text either way — whether parsing yielded nothing, or yielded entries with
  // no usable message.
  return messages.length > 0 ? messages : [raw]
}

export function isValidationError(statusCode?: number): boolean {
  return statusCode === 422
}

/**
 * Sets React Hook Form errors from validation errors.
 * The FieldName property of each validation error should match the form field name.
 *
 * @param errorResponse - The API error response containing validation errors
 * @param setError - The setError function from React Hook Form
 * @returns true if validation errors were set, false otherwise
 */
export function setFormValidationErrors(
  errorResponse: ApiErrorResponse & { status?: number },
  setError: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    name: any,
    error: { type: string; message: string },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: any) => void
): boolean {
  if (errorResponse.status !== 422) return false

  const validationErrors = parseValidationErrors(errorResponse)
  if (validationErrors.length === 0) return false

  let isFirstError = true
  validationErrors.forEach(error => {
    if (error.FieldName) {
      // Set the error for the field matching the FieldName
      // Focus on the first field with an error
      setError(error.FieldName, {
        type: 'server',
        message: error.Message
      }, {
        shouldFocus: isFirstError
      })
      isFirstError = false
    }
  })

  return true
}

export function handleApiError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any,
  showError: (title: string, message: string, duration?: number) => void,
  _showValidationErrors: (errors: ValidationError[], duration?: number) => void,
  defaultTitle: string = 'Operation Failed'
) {
  console.error('API Error:', error)

  // Check if it's a validation error (422) or forbidden (403)
  // Note: The global error handler already shows toasts for these,
  // so we skip showing them here to avoid duplicates
  if (error.status === 422 || isValidationError(error.status)) {
    return
  }
  if (error.status === 403) {
    return
  }

  // Handle other error types
  if (error.error) {
    showError(defaultTitle, error.error)
  } else if (error.message) {
    showError(defaultTitle, error.message)
  } else if (typeof error === 'string') {
    showError(defaultTitle, error)
  } else {
    showError(defaultTitle, 'An unexpected error occurred. Please try again.')
  }
}
